import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./SolutionOverviewByInnovera.module.scss";
import { CaretDownIcon, MoveIcon } from "@/frontend/shared/assets/icons";
import { SectionContentWrapper } from "@/frontend/widgets/SectionContentWrapper/SectionContentWrapper";
import { NormalButton } from "@/frontend/shared/ui/Button";
import clsx from "clsx";
import { InnoveraBlockNoData } from "@/frontend/features/Company/ui/InnoveraBlockNoData/InnoveraBlockNoData";
import { useState } from "react";
import { EDataStatus, TPatentsData, TProductsData } from "@/frontend/features/Company/model/types/types";
import { ProductsUrlsModal } from "@/frontend/features/Company/ui/ProductsUrlsModal/ProductsUrlsModal";
import { SectionLoader } from "../../Section/SectionLoader/SectionLoader";
import { calculateLoadingProgress } from "@/frontend/shared/lib/calculateLoadingProgress/calculateLoadingProgress";
import { TParsingStatus } from "@/libs/models/portfolio-companies";

export type TSolutionOverviewByInnoveraProps = {
  onReportError?: () => void;
  productsData?: TProductsData;
  patentsData?: TPatentsData;
  isLoadingProducts: boolean;
  isLoadingPatents: boolean;
  productsDataStatus?: TParsingStatus | EDataStatus | null;
};

export function SolutionOverviewByInnovera({
  onReportError,
  productsData,
  patentsData,
  isLoadingProducts,
  isLoadingPatents,
  productsDataStatus
}: TSolutionOverviewByInnoveraProps) {
  const [isProductsUrlsModalShown, setIsProductsUrlsModalShown] = useState(false);

  const [isProductsShown, setIsProductsShown] = useState(false);
  const [isPatentsShown, setIsPatentsShown] = useState(false);

  const shownData = (isProductsShown ? productsData?.products : productsData?.products?.slice(0, 3)) || [];

  const shownPatentsData = (isPatentsShown ? patentsData?.patents : patentsData?.patents?.slice(0, 6)) || [];

  const progressPct = calculateLoadingProgress(productsDataStatus, productsData?.createdAt);
  const isProgressBarVisible = productsDataStatus !== "done" && productsDataStatus !== "error" && progressPct <= 100;

  const productsContent =
    shownData?.length > 0 ? (
      <div className={styles.innoveraBlockContentContainer}>
        {shownData?.map((product, index: number) => (
          <a
            className={styles.innoveraBlockContent}
            key={`${product.name}${index}`}
            href={product.imageUrl ?? "#"}
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={product.imageUrl || "/empty_img.svg"}
              alt={product.name}
              className={styles.innoveraBlockContentImage}
            />

            <Typography variant="label-3" color="greyscale900" className={styles.innoveraProductName}>
              {product.name}
            </Typography>
            <Typography variant="body-3" color="greyscale700">
              {product.description}
            </Typography>
          </a>
        ))}
      </div>
    ) : null;

  const patentsContent = (
    <div className={styles.bottomBlockContentContainer}>
      {shownPatentsData?.map((patent) => (
        <div className={styles.patentCard} key={patent.id}>
          <div className={styles.nameLink}>
            <Typography variant="label-4" color="greyscale900" className={styles.tableRowSection}>
              {patent.title}
            </Typography>
            {patent.url && (
              <a href={patent.url} target="_blank" rel="noreferrer" className={styles.patentLink}>
                <MoveIcon className={styles.moveIcon} />
              </a>
            )}
          </div>
          <div className={styles.id}>
            <Typography variant="body-3" color="greyscale600">
              {patent.id}
            </Typography>
          </div>
        </div>
      ))}
    </div>
  );

  const summaryContent =
    productsData?.summary && productsData?.summary.length > 0 ? (
      <div className={styles.summaryDescription}>
        <Typography variant="body-2" color="greyscale900">
          {productsData?.summary}
        </Typography>
      </div>
    ) : null;

  const content = () => {
    if (isLoadingProducts || isLoadingPatents) {
      return <SectionLoader />;
    }
    if (!productsData?.products?.length && !patentsData?.patents?.length) {
      return <InnoveraBlockNoData />;
    }
    return (
      <div className={styles.innoveraBlock}>
        {summaryContent}
        {!!summaryContent && !!productsContent && <div className={styles.divider} />}
        {productsContent}
        {!!productsData?.products && productsData?.products?.length > 3 && !isLoadingProducts && (
          <NormalButton
            variant="primary"
            size="small"
            color="greyscale100"
            className={styles.seeMoreBtn}
            onClick={() => setIsProductsShown((prev) => !prev)}
          >
            <CaretDownIcon
              className={clsx(styles.seeMoreBtnIcon, { [styles.seeMoreBtnIconActive]: isProductsShown })}
            />
            {isProductsShown ? "Hide" : "Show more"}
          </NormalButton>
        )}
        {!!productsContent && !!patentsData?.totalCount && <div className={styles.divider} />}
        {!!patentsData?.totalCount && (
          <div className={styles.innoveraBlockTitle}>
            <div className={styles.innoveraSubBlockTitle} aria-level={7} role="heading">
              <Typography variant="heading-7" color="greyscale900">
                Patents / IP List
              </Typography>
              <Typography variant="heading-7" color="greyscale600">
                {patentsData?.totalCount}
              </Typography>
            </div>
          </div>
        )}
        {!!patentsData?.totalCount && patentsContent}
        {!!patentsData?.totalCount && patentsData?.totalCount > 6 && (
          <NormalButton
            variant="primary"
            size="small"
            color="greyscale100"
            className={styles.seeMoreBtn}
            onClick={() => setIsPatentsShown((prev) => !prev)}
          >
            <CaretDownIcon className={clsx(styles.seeMoreBtnIcon, { [styles.seeMoreBtnIconActive]: isPatentsShown })} />
            {isPatentsShown ? "Hide" : "Show more"}
          </NormalButton>
        )}
        <ProductsUrlsModal
          isOpen={isProductsUrlsModalShown}
          onClose={() => setIsProductsUrlsModalShown(false)}
          urls={productsData?.urls || []}
        />
      </div>
    );
  };
  return (
    <SectionContentWrapper
      isByInnovera
      onReportError={onReportError}
      progress={{ visible: isProgressBarVisible, pct: progressPct }}
    >
      {content()}
    </SectionContentWrapper>
  );
}
